import type { UseTranslationResponse } from 'react-i18next';
import { useTranslation } from 'react-i18next';

export const NS = 'departments-page';
export function useDepartmentsPageTranslation(): UseTranslationResponse<typeof NS> {
  return useTranslation(NS);
}

const hintKeyPrefix = 'hint';
export function useDepartmentHintTranslation(): UseTranslationResponse<typeof NS, typeof hintKeyPrefix> {
  return useTranslation(NS, { keyPrefix: hintKeyPrefix });
}

export const ruRu = ({
  [NS]: {
    empty_tree: 'У вас пока нет подразделений',
    empty_page: 'Выберите подразделение, чтобы увидеть его детали',
    title: 'Подразделения',
    geo_zone: {
      title: 'Зона на карте',
    },
    hint: {
      title: 'Полезно знать',
      intro: 'Теги доступа предназначены для предоставления доступа пользователям к событиям и их источникам (камерам).',
      access: {
        title: 'Как организовать доступ:',
        item1: 'Назначить теги доступа группам источников',
        item2: 'Эти же теги добавить в подразделение и установить признак «Назначить всем в подразделении»',
      },
      lists: {
        title: 'Списки',
        owner: 'Когда подразделение является владельцем списка и для него установлен признак «Назначить всем в подразделении», его пользователи могут редактировать и список, и карточки персон в нем.',
        view: 'Если подразделение имеет доступ на просмотр списка и для него установлен признак «Назначить всем в подразделении», его пользователи могут просматривать список и карточки персон в нем.',
      },
      users: {
        title: 'Пользователи',
        text: 'Здесь отображается и редактируется состав пользователей подразделения.',
      },
      streamGroups: {
        title: 'Группы источников',
        text: 'Указанные здесь группы источников принадлежат подразделению, то есть только пользователи этого подразделения могут редактировать группы и источники в них.',
      },
    },
  },
}) as Record<string, unknown>;
export const en = ({
  [NS]: {
    empty_tree: 'You do not have departments yet',
    empty_page: 'Select a department to view its details',
    title: 'Departments',
    geo_zone: {
      title: 'Zone on the map',
    },
    hint: {
      title: 'Good to know',
      intro: 'Access tags are designed to grant users access to events and their sources (cameras).',
      access: {
        title: 'How to organize access:',
        item1: 'Assign access tags to source groups',
        item2: 'Add the same tags to the department and enable «Assign to everyone in the department»',
      },
      lists: {
        title: 'Lists',
        owner: 'When a department owns a list and the «Assign to everyone in the department» option is enabled, its users can edit both the list and the person cards in it.',
        view: 'If a department has view access to a list and the «Assign to everyone in the department» option is enabled, its users can view the list and the person cards in it.',
      },
      users: {
        title: 'Users',
        text: 'Here you can view and edit the members of the department.',
      },
      streamGroups: {
        title: 'Source groups',
        text: 'The source groups listed here belong to the department, meaning only users of this department can edit the groups and the sources in them.',
      },
    },
  },
}) as Record<string, unknown>;
export const ptBr = ({
  [NS]: {
    empty_tree: 'Você ainda não tem departamentos',
    empty_page: 'Selecione um departamento para ver os detalhes',
    title: 'Departamentos',
    geo_zone: {
      title: 'Zona no mapa',
    },
    hint: {
      title: 'Bom saber',
      intro: 'As tags de acesso destinam-se a conceder aos usuários acesso aos eventos e às suas fontes (câmeras).',
      access: {
        title: 'Como organizar o acesso:',
        item1: 'Atribuir tags de acesso aos grupos de fontes',
        item2: 'Adicionar as mesmas tags ao departamento e ativar a opção «Atribuir a todos no departamento»',
      },
      lists: {
        title: 'Listas',
        owner: 'Quando um departamento é proprietário de uma lista e a opção «Atribuir a todos no departamento» está ativada, seus usuários podem editar tanto a lista quanto os cartões de pessoas nela.',
        view: 'Se um departamento tem acesso de visualização a uma lista e a opção «Atribuir a todos no departamento» está ativada, seus usuários podem visualizar a lista e os cartões de pessoas nela.',
      },
      users: {
        title: 'Usuários',
        text: 'Aqui é exibida e editada a composição de usuários do departamento.',
      },
      streamGroups: {
        title: 'Grupos de fontes',
        text: 'Os grupos de fontes indicados aqui pertencem ao departamento, ou seja, apenas os usuários deste departamento podem editar os grupos e as fontes neles.',
      },
    },
  },
}) as Record<string, unknown>;
export const esEs = ({
  [NS]: {
    empty_tree: 'Aún no tienes subdivisiones',
    empty_page: 'Seleccione un departamento para ver sus detalles',
    title: 'Departamentos',
    geo_zone: {
      title: 'Zona en el mapa',
    },
    hint: {
      title: 'Bueno saber',
      intro: 'Las etiquetas de acceso están diseñadas para otorgar a los usuarios acceso a los eventos y sus fuentes (cámaras).',
      access: {
        title: 'Cómo organizar el acceso:',
        item1: 'Asignar etiquetas de acceso a los grupos de fuentes',
        item2: 'Añadir las mismas etiquetas a la subdivisión y activar la opción «Asignar a todos en la subdivisión»',
      },
      lists: {
        title: 'Listas',
        owner: 'Cuando una subdivisión es propietaria de una lista y la opción «Asignar a todos en la subdivisión» está activada, sus usuarios pueden editar tanto la lista como las tarjetas de personas en ella.',
        view: 'Si una subdivisión tiene acceso de visualización a una lista y la opción «Asignar a todos en la subdivisión» está activada, sus usuarios pueden ver la lista y las tarjetas de personas en ella.',
      },
      users: {
        title: 'Usuarios',
        text: 'Aquí se muestra y se edita la composición de usuarios de la subdivisión.',
      },
      streamGroups: {
        title: 'Grupos de fuentes',
        text: 'Los grupos de fuentes indicados aquí pertenecen a la subdivisión, es decir, solo los usuarios de esta subdivisión pueden editar los grupos y las fuentes en ellos.',
      },
    },
  },
}) as Record<string, unknown>;
